import { useRouter } from 'next/router'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { CMSPlanDetail, InformationsDetailPlan } from '@/types/plans'

import { useAmplitude } from '../AmplitudeProvider'
import { useCouponContext } from '../coupon'
import { PlanFromCMS } from './fetchPlansDetailsFromCMS'
import { getInitialCurrentPlan } from './getInitialCurrentPlan'
import { mergePlanDetails } from './mergePlanDetails'

export type PlanDetails = ReturnType<typeof mergePlanDetails>

interface Context {
  plansByName: Map<string, PlanDetails>
  filteredPlansByName: Map<string, PlanDetails>
  plansByType: Map<'transactional' | 'tap_phone', PlanDetails[]>
  plansByGroupTier: Map<string, PlanDetails[]>
  currentPlan: PlanDetails | null
  setCurrentPlan(plan: PlanDetails): void
}

const initialContext: Context = {
  currentPlan: null,
  plansByName: new Map(),
  plansByType: new Map(),
  plansByGroupTier: new Map(),
  filteredPlansByName: new Map(),
  setCurrentPlan: () => {}
}

const TonPlansContext = React.createContext(initialContext)

export const useTonPlans = () => {
  return useContext(TonPlansContext)
}

export interface TonPlansProviderProps {
  children: React.ReactNode
  defaultPlan?: CMSPlanDetail
  plansDetailsFromCMS: PlanFromCMS[]
  plansDetailsFromAPI: InformationsDetailPlan[]
}

export const TonPlansProvider: React.FC<TonPlansProviderProps> = ({
  children,
  defaultPlan,
  plansDetailsFromCMS,
  plansDetailsFromAPI
}) => {
  const router = useRouter()
  const { experiment } = useAmplitude()
  const { referrerTierID } = useCouponContext()
  const [currentPlan, setCurrentPlan] = useState(initialContext.currentPlan)

  const plansByName = useMemo(() => {
    try {
      const plansDetailsFromAPIMap = new Map(
        plansDetailsFromAPI.map((plan) => [plan.name, plan])
      )

      const map = new Map<string, PlanDetails>()
      const plansGroupTierCounter: Record<string, number> = {}
      const plansGroupByTier = Map.groupBy(
        plansDetailsFromCMS,
        (plan) => plan.groupTier
      )

      for (const planFromCMS of plansDetailsFromCMS) {
        plansGroupTierCounter[planFromCMS.groupTier] = 0
      }

      for (const planFromCMS of plansDetailsFromCMS) {
        const planTierName = planFromCMS.name

        const planFromAPI =
          plansDetailsFromAPIMap.get(planFromCMS.name) ||
          ({} as InformationsDetailPlan)

        const planMerged = mergePlanDetails(planFromAPI, planFromCMS)

        const { variantName, amplitude_experiment } =
          planMerged.experiment || {}
        const { key: experimentID = '' } = amplitude_experiment || {}

        const { value: activeVariant } = experiment?.variant(experimentID) || {}

        const isEqualVariant = activeVariant === variantName
        const containsVariant =
          variantName && activeVariant && variantName.includes(activeVariant)

        if (isEqualVariant || containsVariant) {
          map.set(planTierName, planMerged)
          plansGroupTierCounter[planFromCMS.groupTier]++
        }
      }

      for (const groupTier in plansGroupTierCounter) {
        const value = plansGroupTierCounter[groupTier]

        if (value > 0) continue

        const plans = plansGroupByTier.get(groupTier) || []

        const plansWithControlVariant = plans.filter(
          (plan) => plan.experiment?.isControl
        )

        plansWithControlVariant.forEach((plan) => {
          const planFromAPI =
            plansDetailsFromAPIMap.get(plan.name) ||
            ({} as InformationsDetailPlan)

          const planMerged = mergePlanDetails(
            planFromAPI,

            plan as unknown as PlanFromCMS
          )

          map.set(planMerged.name, planMerged)
        })
      }

      const sortedPlans = Array.from(map.values()).sort(
        (a, b) => a.order - b.order
      )

      return new Map(sortedPlans.map((plan) => [plan.name, plan]))
    } catch (error) {
      console.error('Error processing plans:', error)

      return new Map<string, PlanDetails>()
    }
  }, [experiment, plansDetailsFromAPI, plansDetailsFromCMS])

  const filteredPlansByName = useMemo(() => {
    const newPlansByName = new Map(plansByName)

    for (const plan of newPlansByName.values()) {
      if (!referrerTierID && plan.hidden) {
        newPlansByName.delete(plan.name)

        continue
      }

      if (!referrerTierID) continue

      if (plan.rexTierIDsInclude?.includes(referrerTierID)) {
        plan.hidden = false

        newPlansByName.set(plan.name, plan)

        continue
      }

      const canRemovePlan = plan.rexTierIDsExclude?.includes(referrerTierID)

      if (canRemovePlan || (!canRemovePlan && plan.hidden)) {
        newPlansByName.delete(plan.name)

        continue
      }
    }

    return newPlansByName
  }, [plansByName, referrerTierID])

  const plansByType: Context['plansByType'] = useMemo(() => {
    const plans = new Map<'transactional' | 'tap_phone', PlanDetails[]>()

    for (const plan of filteredPlansByName.values()) {
      if (!plans.has(plan.type)) {
        plans.set(plan.type, [])
      }

      const items = [...plans.get(plan.type)!]

      items.push(plan)

      plans.set(plan.type, items)
    }

    return plans
  }, [filteredPlansByName])

  const plansByGroupTier = useMemo(() => {
    const plans = new Map<string, PlanDetails[]>()

    for (const plan of plansByName.values()) {
      if (!plans.has(plan.groupTier)) {
        plans.set(plan.groupTier, [])
      }

      const items = [...plans.get(plan.groupTier)!]

      items.push(plan)

      plans.set(plan.groupTier, items)
    }

    return plans
  }, [plansByName])

  useEffect(() => {
    const queryStringTaxValue = Array.isArray(router.query.tax)
      ? router.query.tax[0]
      : router.query.tax

    const defaultTier = defaultPlan?.tiers?.find((tier) => tier.isDefaultTier)
    const newCurrentPlanTier = plansByName.get(defaultTier?.tierName || '')

    if (newCurrentPlanTier) {
      setCurrentPlan(newCurrentPlanTier)

      return
    }

    const allPlans = Array.from(filteredPlansByName.values())
    const newCurrentPlan = getInitialCurrentPlan(allPlans, queryStringTaxValue)

    if (newCurrentPlan) {
      setCurrentPlan(newCurrentPlan)
    }
  }, [defaultPlan?.tiers, filteredPlansByName, plansByName, router.query.tax])

  return (
    <TonPlansContext.Provider
      value={{
        currentPlan,
        setCurrentPlan,
        plansByName,
        plansByType,
        plansByGroupTier,
        filteredPlansByName
      }}
    >
      {children}
    </TonPlansContext.Provider>
  )
}
